import { z } from 'zod';

/**
 * People: the contacts found at the businesses a research run collected.
 *
 * People are discovered while enriching company websites (team/about/contact pages), so every
 * person is tied to the account they were found for and carries the page they came from. There
 * is no people-only discovery source in this build, which is why a person always has a company.
 */
export const PEOPLE_SORT_FIELDS = ['fullName', 'title', 'company', 'createdAt', 'updatedAt'] as const;
export type PeopleSortField = (typeof PEOPLE_SORT_FIELDS)[number];

export const PEOPLE_SORT_LABELS: Record<PeopleSortField, string> = {
  fullName: 'Name',
  title: 'Job title',
  company: 'Company',
  createdAt: 'First seen',
  updatedAt: 'Last updated',
};

export const PersonQuerySchema = z.object({
  projectId: z.string().max(64).optional(),
  accountId: z.string().max(64).optional(),
  search: z.string().trim().max(200).optional(),
  /** Role families (see ROLE_FAMILIES in custom-attributes.ts), e.g. "hr", "marketing". */
  roles: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
  hasEmail: z.boolean().optional(),
  hasPhone: z.boolean().optional(),
  sort: z.enum(PEOPLE_SORT_FIELDS).default('fullName'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  offset: z.number().int().min(0).max(100_000).default(0),
  limit: z.number().int().min(1).max(200).default(50),
});
export type PersonQueryRequest = z.input<typeof PersonQuerySchema>;

export interface PersonRowDto {
  id: string;
  accountId: string | null;
  /** Denormalized for display so the table needs no second request. */
  companyName: string | null;
  companyCity: string | null;
  companyWebsite: string | null;
  fullName: string;
  title: string | null;
  department: string | null;
  location: string | null;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  roles: string[];
  source: string;
  sourceUrl: string | null;
  /** How the value was obtained — shown so a reader can judge it (see ValueMethod). */
  method: string;
  createdAt: number;
  updatedAt: number;
}

export interface PersonQueryResponse {
  items: PersonRowDto[];
  total: number;
  offset: number;
  limit: number;
}

/** Export columns used when exporting the people table. */
export function defaultPeopleExportHeaders(): string[] {
  return ['Name', 'Job title', 'Company', 'City', 'Email', 'Phone', 'LinkedIn', 'Found on', 'How'];
}
